import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBanners } from '../../redux/apis/BannerApi';

export default function HeroSlider() {
  const dispatch = useDispatch();
  const { banners, loading } = useSelector(state => state.BannerSlice);
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = banners.length || 2;

  // Fetch banners on mount
  useEffect(() => {
    dispatch(fetchBanners());
  }, [dispatch]);

  // Auto-slide every 6 seconds
  useEffect(() => {
    if (banners.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 6000);
    return () => clearInterval(interval);
  }, [currentSlide, banners.length, totalSlides]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  // Show loading state
  if (loading || banners.length === 0) {
    return (
      <div 
        className="d-flex align-items-center justify-content-center"
        style={{
          backgroundColor: 'var(--sand-100)',
          minHeight: '80vh'
        }}
      >
        <div className="text-center">
          <div className="spinner-border text-secondary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3" style={{ color: 'var(--sand-700)' }}>Loading banners...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="position-relative"
      style={{
        backgroundColor: 'var(--sand-100)',
        minHeight: '80vh',
        overflow: 'hidden'
      }}
    >
      {/* Slides Container */}
      <div 
        className="d-flex"
        style={{
          width: `${banners.length * 100}%`,
          transform: `translateX(-${currentSlide * (100 / banners.length)}%)`,
          transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: '80vh'
        }}
      >
        {banners.map((banner) => (
          <div
            key={banner._id}
            style={{
              width: `${100 / banners.length}%`,
              minHeight: '80vh',
              paddingTop: '80px',
              flexShrink: 0
            }}
          >
            {banner.type === 'modern' ? (
              // Modern Creative Design Slide - Inspired by organic shapes
              <div style={{ minHeight: '80vh', backgroundColor: 'var(--sand-200)' }} className="d-flex align-items-center position-relative overflow-hidden">
                {/* Large Decorative Circle - Top Right */}
                <div
                  className="position-absolute d-none d-lg-block"
                  style={{
                    top: '-100px',
                    right: '-100px',
                    width: '400px',
                    height: '400px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(226, 211, 166, 0.3), rgba(209, 181, 115, 0.4))',
                    border: '2px solid var(--sand-500)',
                    zIndex: 0
                  }}
                />

                {/* Medium Circle - Bottom Right */}
                <div
                  className="position-absolute d-none d-lg-block"
                  style={{
                    bottom: '10%',
                    right: '5%',
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--sand-400)',
                    opacity: 0.4,
                    zIndex: 0
                  }}
                />

                {/* Small Accent Circle */}
                <div
                  className="position-absolute d-none d-lg-block"
                  style={{
                    top: '20%',
                    right: '12%',
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--sand-600)',
                    opacity: 0.6,
                    zIndex: 0
                  }}
                />

                {/* Decorative Dots - Left Side */}
                <div className="position-absolute d-none d-md-flex flex-column gap-3" style={{ left: '30px', top: '50%', transform: 'translateY(-50%)', zIndex: 1 }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'var(--sand-700)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'var(--sand-700)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'var(--sand-700)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'var(--sand-700)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} />
                </div>

                {/* Diagonal Lines Decoration - Top Right */}
                <div className="position-absolute d-none d-lg-block" style={{ top: '12%', right: '18%', zIndex: 1 }}>
                  <div style={{ width: '80px', height: '3px', backgroundColor: 'var(--sand-700)', marginBottom: '12px', transform: 'rotate(-45deg)', opacity: 0.7 }} />
                  <div style={{ width: '60px', height: '3px', backgroundColor: 'var(--sand-700)', transform: 'rotate(-45deg)', opacity: 0.7 }} />
                </div>

                <div className="container px-2 px-sm-3" style={{ position: 'relative', zIndex: 2 }}>
                  <div className="row align-items-center">
                    {/* Left Side - Product Image with Modern Frame */}
                    <div className="col-lg-6 mb-5 mb-lg-0 px-4 px-sm-5">
                      <div className="position-relative d-flex justify-content-center" style={{ padding: '0 10px' }}>
                        {/* Main Image Container with Shadow */}
                        <div
                          className="position-relative"
                          style={{
                            width: 'min(340px, 75vw)',
                            height: 'min(420px, 80vw)',
                            borderRadius: '25px',
                            overflow: 'visible'
                          }}
                        >
                          {/* Image with Border */}
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              border: '5px solid var(--sand-700)',
                              borderRadius: '25px',
                              overflow: 'hidden',
                              boxShadow: window.innerWidth < 768 ? '10px 10px 0px rgba(179, 135, 63, 0.25)' : 'clamp(15px, 4vw, 25px) clamp(15px, 4vw, 25px) 0px rgba(179, 135, 63, 0.25)',
                              position: 'relative',
                              backgroundColor: 'white'
                            }}
                          >
                            {banner.image ? (
                              <img
                                src={banner.image}
                                alt={banner.title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  objectPosition: 'center'
                                }}
                              />
                            ) : (
                              <div style={{
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(135deg, rgba(240, 233, 209, 0.5), rgba(226, 211, 166, 0.7))',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <img
                                  src="/product1.jpg"
                                  alt="Perfume Collection"
                                  style={{
                                    width: '80%',
                                    height: '80%',
                                    objectFit: 'contain'
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {/* Decorative Badge - Bottom Left */}
                          <div
                            className="position-absolute d-none d-md-flex"
                            style={{
                              bottom: '-25px',
                              left: '-25px',
                              width: '140px',
                              height: '140px',
                              borderRadius: '50%',
                              backgroundColor: 'var(--sand-600)',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
                              zIndex: 10,
                              border: '4px solid white'
                            }}
                          >
                            <div className="text-center">
                              <div style={{ fontSize: '2.5rem', marginBottom: '5px' }}>✨</div>
                              <div style={{ fontSize: '0.75rem', color: 'white', fontWeight: '700', letterSpacing: '1px' }}>
                                LUXURY
                              </div>
                            </div>
                          </div>

                          {/* Small Decorative Element - Top Right */}
                          <div
                            className="position-absolute d-none d-lg-block"
                            style={{
                              top: '-15px',
                              right: '-15px',
                              width: '60px',
                              height: '60px',
                              borderRadius: '50%',
                              backgroundColor: 'var(--sand-500)',
                              border: '3px solid white',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              zIndex: 10
                            }}
                          />
                        </div>

                        {/* Website URL - Bottom */}
                        <div
                          className="position-absolute d-none d-lg-block"
                          style={{
                            bottom: '-50px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: '0.85rem',
                            color: 'var(--sand-800)',
                            fontWeight: '600',
                            letterSpacing: '0.5px',
                            zIndex: 5
                          }}
                        >
                          {banner.websiteUrl || 'www.vamanaperfumes.com'}
                        </div>
                      </div>
                    </div>

                    {/* Right Side - Text Content */}
                    <div className="col-lg-6 px-2 px-sm-3 px-md-4 px-lg-5">
                      {/* Brand Logo - Top */}
                      <div className="mb-4 d-none d-lg-block">
                        <div style={{
                          fontSize: '1.1rem',
                          fontWeight: '800',
                          color: 'var(--sand-900)',
                          letterSpacing: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <span style={{ fontSize: '1.5rem' }}>⚜️</span>
                          VAMANA
                        </div>
                      </div>

                      {/* Top Badge */}
                      <div className="mb-3">
                        <div
                          style={{
                            color: 'var(--sand-700)',
                            fontSize: 'clamp(0.95rem, 2vw, 1.15rem)',
                            fontWeight: '500',
                            fontStyle: 'italic',
                            letterSpacing: '0.5px'
                          }}
                        >
                          {banner.topBadge}
                        </div>
                      </div>

                      {/* Main Heading */}
                      <h1
                        className="fw-bold mb-4"
                        style={{
                          color: 'var(--sand-900)',
                          fontFamily: "'Playfair Display', serif",
                          lineHeight: '1.1',
                          fontSize: 'clamp(2.5rem, 7vw, 4.5rem)',
                          whiteSpace: 'pre-line',
                          letterSpacing: '-2px',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.05)'
                        }}
                      >
                        {banner.title}
                      </h1>

                      {/* Subtitle */}
                      <p
                        className="mb-5"
                        style={{
                          color: 'var(--sand-800)',
                          maxWidth: '480px',
                          lineHeight: '1.8',
                          fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                          fontWeight: '400'
                        }}
                      >
                        {banner.subtitle}
                      </p>

                      {/* Bottom Info with Presenter */}
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        {banner.bottomBadge && (
                          <div style={{
                            padding: '12px 24px',
                            backgroundColor: 'var(--sand-300)',
                            borderRadius: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          }}>
                            <span style={{ color: 'var(--sand-700)', fontSize: '0.9rem', fontWeight: '500' }}>
                              Presented By:
                            </span>
                            <span style={{
                              color: 'var(--sand-900)',
                              fontSize: '1rem',
                              fontWeight: '700'
                            }}>
                              {banner.bottomBadge}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Decorative Lines - Bottom */}
                      <div className="d-none d-xl-block mt-5">
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                          <div style={{ width: '60px', height: '3px', backgroundColor: 'var(--sand-700)' }} />
                          <div style={{ width: '40px', height: '3px', backgroundColor: 'var(--sand-600)' }} />
                          <div style={{ width: '20px', height: '3px', backgroundColor: 'var(--sand-500)' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : banner.type === 'circle' ? (
              // Circle Design Slide
              <div style={{ minHeight: '80vh' }} className="d-flex align-items-center position-relative">
                <div className="container px-2 px-sm-3">
                  <div className="row align-items-center">
                    {/* Left Side - Text Content */}
                    <div className="col-lg-6 mb-5 mb-lg-0 px-2 px-sm-3 px-md-4 px-lg-3">
                      {/* Brand Badge */}
                      <div className="mb-3 mb-md-4">
                        <div 
                          className="px-3 px-sm-4 py-2 rounded-pill mt-4 mt-lg-0"
                          style={{
                            border: `2px solid var(--sand-700)`,
                            color: 'var(--sand-800)',
                            fontSize: 'clamp(0.7rem, 2vw, 0.9rem)',
                            fontWeight: '500',
                            maxWidth: '100%',
                            width: 'fit-content',
                            textAlign: 'center',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.5',
                            display: 'inline-block'
                          }}
                        >
                          {banner.topBadge}
                        </div>
                      </div>

                      {/* Main Heading */}
                      <h1 
                        className="fw-bold mb-3 mb-md-4"
                        style={{
                          color: 'var(--sand-900)',
                          fontFamily: "'Playfair Display', serif",
                          lineHeight: '1.2',
                          fontSize: 'clamp(1.8rem, 6vw, 3.5rem)',
                          whiteSpace: 'pre-line'
                        }}
                      >
                        {banner.title}
                      </h1>

                      {/* Subtitle */}
                      <p 
                        style={{
                          color: 'var(--sand-700)',
                          maxWidth: '480px',
                          lineHeight: '1.6',
                          fontSize: 'clamp(0.85rem, 2.5vw, 1.25rem)'
                        }}
                        className="mb-3 mb-md-4 mb-lg-5"
                      >
                        {banner.subtitle}
                      </p>

                      {/* Bottom Badge */}
                      {banner.bottomBadge && (
                        <div>
                          <div 
                            className="px-3 px-sm-4 py-2 rounded-pill mt-4 mt-lg-0"
                            style={{
                              border: `2px solid var(--sand-700)`,
                              color: 'var(--sand-800)',
                              fontSize: 'clamp(0.7rem, 2vw, 0.9rem)',
                              maxWidth: '100%',
                              width: 'fit-content',
                              textAlign: 'center',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal',
                              lineHeight: '1.5',
                              display: 'inline-block'
                            }}
                          >
                            {banner.bottomBadge}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Side - Circular Product Display */}
                    <div className="col-lg-6 position-relative">
                      <div className="position-relative d-flex justify-content-center" style={{ minHeight: '500px' }}>
                        {/* Arrow Icon - Top Right */}
                        <div 
                          className="position-absolute d-none d-lg-block"
                          style={{
                            top: '0',
                            right: '10%',
                            fontSize: 'clamp(2rem, 3vw, 3rem)',
                            color: 'var(--sand-900)',
                            transform: 'rotate(45deg)'
                          }}
                        >
                          →
                        </div>

                        {/* Main Square Container with Curved Border */}
                        <div 
                          className="position-relative"
                          style={{
                            width: 'min(450px, 90vw)',
                            height: 'min(450px, 90vw)',
                            maxWidth: '500px',
                            maxHeight: '500px'
                          }}
                        >
                          {/* Double Square Border with Curved Corners */}
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '30px',
                              border: `3px solid var(--sand-700)`,
                              position: 'absolute',
                              top: '0',
                              left: '0'
                            }}
                          />
                          <div
                            style={{
                              width: '96%',
                              height: '96%',
                              borderRadius: '28px',
                              border: `2px solid var(--sand-600)`,
                              position: 'absolute',
                              top: '2%',
                              left: '2%'
                            }}
                          />

                          {/* Inner Square Background with Image Fill */}
                          <div
                            style={{
                              width: '90%',
                              height: '90%',
                              borderRadius: '25px',
                              position: 'absolute',
                              top: '5%',
                              left: '5%',
                              overflow: 'hidden',
                              background: 'linear-gradient(135deg, rgba(240, 233, 209, 0.3), rgba(226, 211, 166, 0.5))'
                            }}
                          >
                            <div 
                              className="d-flex align-items-center justify-content-center h-100 w-100"
                              style={{
                                position: 'relative'
                              }}
                            >
                              {banner.image ? (
                                <img
                                  src={banner.image}
                                  alt={banner.title}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center'
                                  }}
                                />
                              ) : (
                                <img
                                  src="/product1.jpg"
                                  alt="Perfume Collection"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center'
                                  }}
                                />
                              )}
                            </div>
                          </div>

                          {/* Decorative Leaves - Right Side of Square */}
                          <img
                            src="/leaf.png"
                            alt="Leaf"
                            className="position-absolute d-none d-md-block"
                            style={{
                              top: '15%',
                              right: '-15%',
                              width: 'clamp(80px, 15vw, 150px)',
                              height: 'auto',
                              objectFit: 'contain',
                              transform: 'rotate(25deg)',
                              opacity: '0.85',
                              zIndex: 5
                            }}
                          />
                          <img
                            src="/leaf.png"
                            alt="Leaf"
                            className="position-absolute d-none d-md-block"
                            style={{
                              top: '50%',
                              right: '-18%',
                              transform: 'translateY(-50%) rotate(-15deg)',
                              width: 'clamp(70px, 13vw, 130px)',
                              height: 'auto',
                              objectFit: 'contain',
                              opacity: '0.8',
                              zIndex: 5
                            }}
                          />
                          <img
                            src="/leaf.png"
                            alt="Leaf"
                            className="position-absolute d-none d-md-block"
                            style={{
                              bottom: '20%',
                              right: '-14%',
                              width: 'clamp(75px, 14vw, 140px)',
                              height: 'auto',
                              objectFit: 'contain',
                              transform: 'rotate(40deg)',
                              opacity: '0.88',
                              zIndex: 5
                            }}
                          />
                        </div>

                        {/* Website URL - Bottom Right */}
                        <div
                          className="position-absolute d-none d-lg-block"
                          style={{
                            bottom: '20px',
                            right: '10%',
                            fontSize: '0.85rem',
                            color: 'var(--sand-800)',
                            fontWeight: '500'
                          }}
                        >
                          {banner.websiteUrl}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Arch Design Slide
              <div style={{ minHeight: '80vh' }} className="d-flex align-items-center position-relative">
                <div className="container px-2 px-sm-3">
                  <div className="row align-items-center">
                    {/* Left Side - Arch Shape with Product */}
                    <div className="col-lg-6 mb-5 mb-lg-0">
                      <div className="position-relative d-flex justify-content-center" style={{ minHeight: '500px' }}>
                        {/* Dotted Circle Decoration - Top Left */}
                        <div
                          className="d-none d-lg-block"
                          style={{
                            position: 'absolute',
                            top: '-20px',
                            left: '5%',
                            width: 'clamp(120px, 15vw, 180px)',
                            height: 'clamp(120px, 15vw, 180px)',
                            borderRadius: '50%',
                            border: '3px dotted var(--sand-700)',
                            zIndex: 0
                          }}
                        >
                          {/* Yellow Flower Inside Dotted Circle */}
                          <img
                            src="/yellowflow.png"
                            alt="Yellow Flower"
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '70%',
                              height: '70%',
                              objectFit: 'contain',
                              zIndex: 1
                            }}
                          />
                        </div>

                        {/* Arch Shape Container */}
                        <div
                          className="position-relative"
                          style={{
                            width: 'min(400px, 85vw)',
                            height: 'min(500px, 100vw)',
                            marginTop: '40px'
                          }}
                        >
                          {/* Arch Background */}
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '50% 50% 0 0',
                              background: 'linear-gradient(135deg, rgba(226, 211, 166, 0.4), rgba(209, 181, 115, 0.5))',
                              border: `3px solid var(--sand-700)`,
                              overflow: 'hidden',
                              position: 'relative'
                            }}
                          >
                            {/* Product Image Inside Arch */}
                            {banner.image ? (
                              <img
                                src={banner.image}
                                alt={banner.title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  objectPosition: 'center'
                                }}
                              />
                            ) : (
                              <img
                                src="/product2.jpg"
                                alt="Perfume Product"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  objectPosition: 'center'
                                }}
                              />
                            )}
                          </div>

                          {/* Decorative Flower at Bottom */}
                          <img
                            src="/flower.jpg"
                            alt="Flower"
                            className="d-none d-md-block"
                            style={{
                              position: 'absolute',
                              bottom: '-30px',
                              left: '20px',
                              width: 'clamp(80px, 12vw, 120px)',
                              height: 'clamp(80px, 12vw, 120px)',
                              objectFit: 'contain',
                              zIndex: 10
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Right Side - Text Content */}
                    <div className="col-lg-6 text-center text-lg-start position-relative px-2 px-sm-3 px-md-4 px-lg-3">
                      {/* Top Badge */}
                      <div className="mb-3 mb-md-4">
                        <div
                          className="d-inline-block px-3 px-sm-4 py-2 rounded-pill mt-4 mt-lg-0"
                          style={{
                            backgroundColor: 'var(--sand-400)',
                            color: 'var(--sand-900)',
                            fontSize: 'clamp(0.7rem, 2vw, 0.9rem)',
                            fontWeight: '600',
                            maxWidth: '100%',
                            width: 'fit-content',
                            textAlign: 'center',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.5'
                          }}
                        >
                          {banner.topBadge}
                        </div>
                      </div>

                      {/* Main Heading */}
                      <h1 
                        className="fw-bold mb-3 mb-md-4"
                        style={{
                          color: 'var(--sand-900)',
                          fontFamily: "'Playfair Display', serif",
                          lineHeight: '1.2',
                          fontSize: 'clamp(1.8rem, 6vw, 3.5rem)',
                          whiteSpace: 'pre-line'
                        }}
                      >
                        {banner.title}
                      </h1>

                      {/* Subtitle */}
                      <p 
                        className="mb-3 mb-md-4 mb-lg-5"
                        style={{
                          color: 'var(--sand-800)',
                          fontWeight: '600',
                          maxWidth: '500px',
                          margin: '0 auto 2rem',
                          textAlign: 'inherit',
                          fontSize: 'clamp(0.85rem, 2.5vw, 1.25rem)'
                        }}
                      >
                        {banner.subtitle}
                      </p>

                      {/* Bottom Info */}
                      <div className="mb-3 mb-md-4">
                        <p style={{ color: 'var(--sand-700)', fontSize: 'clamp(0.7rem, 1.8vw, 0.9rem)', marginBottom: '0.5rem' }}>
                          {banner.websiteUrl}
                        </p>
                        {banner.bottomText && (
                          <p style={{ color: 'var(--sand-800)', fontSize: 'clamp(0.7rem, 1.8vw, 0.9rem)' }}>
                            {banner.bottomText}
                          </p>
                        )}
                      </div>

                      {/* Decorative Lines - Bottom Right */}
                      <div 
                        className="position-absolute d-none d-xl-block"
                        style={{
                          bottom: '-50px',
                          right: '50px',
                          width: '200px',
                          height: '100px'
                        }}
                      >
                        {/* Horizontal Lines */}
                        <div style={{ borderTop: `2px solid var(--sand-700)`, marginBottom: '15px' }} />
                        <div style={{ borderTop: `2px solid var(--sand-700)`, marginBottom: '15px' }} />
                        <div style={{ borderTop: `2px solid var(--sand-700)`, marginBottom: '15px' }} />
                        
                        {/* Fan Lines */}
                        <div style={{ position: 'relative', height: '60px', marginTop: '20px' }}>
                          {[0, 15, 30, 45, 60, 75, 90].map((angle, i) => (
                            <div
                              key={i}
                              style={{
                                position: 'absolute',
                                bottom: '0',
                                left: '50%',
                                width: '2px',
                                height: '50px',
                                backgroundColor: 'var(--sand-700)',
                                transformOrigin: 'bottom',
                                transform: `rotate(${angle - 45}deg)`
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="position-fixed btn btn-lg rounded-circle shadow d-none d-md-flex"
        style={{
          left: 'clamp(10px, 2vw, 30px)',
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'var(--sand-600)',
          border: 'none',
          color: 'white',
          width: 'clamp(40px, 5vw, 50px)',
          height: 'clamp(40px, 5vw, 50px)',
          zIndex: 1000,
          transition: 'all 0.3s ease',
          fontSize: '1.5rem',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'var(--sand-700)';
          e.target.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'var(--sand-600)';
          e.target.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        ‹
      </button>

      <button
        onClick={nextSlide}
        className="position-fixed btn btn-lg rounded-circle shadow d-none d-md-flex"
        style={{
          right: 'clamp(10px, 2vw, 30px)',
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'var(--sand-600)',
          border: 'none',
          color: 'white',
          width: 'clamp(40px, 5vw, 50px)',
          height: 'clamp(40px, 5vw, 50px)',
          zIndex: 1000,
          transition: 'all 0.3s ease',
          fontSize: '1.5rem',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'var(--sand-700)';
          e.target.style.transform = 'translateY(-50%) scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'var(--sand-600)';
          e.target.style.transform = 'translateY(-50%) scale(1)';
        }}
      >
        ›
      </button>
    </div>
  );
}
